import type { SoftwareDeviceType, SoftwareItem, SoftwareRankingData, SoftwareToolCategory } from '../types/software'

/** 仅接受普通接口对象，避免空响应和数组被当作网关包装层。 */
const isSoftwareRecord = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** 兼容完整网关响应与 request 已解包的结果，并显式暴露业务失败。 */
export const unwrapSoftwarePayload = (response: unknown): unknown => {
  let payload = response
  for (let depth = 0; depth < 4 && isSoftwareRecord(payload); depth += 1) {
    if (payload.succeed === false || payload.success === false) {
      throw new Error('Software API returned an unsuccessful response')
    }
    if ('data' in payload) {
      payload = payload.data
    } else if ('Data' in payload) {
      payload = payload.Data
    } else {
      break
    }
  }
  return payload
}

/** 接口标识偶尔为数字，仅转换标量，避免把异常对象显示为文案。 */
const getSoftwareText = (value: unknown): string => {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
}

/** 数值字符串保留原值，其余不合法指标留空。 */
const getSoftwareMetric = (value: unknown): number | string | undefined => {
  return typeof value === 'string' || typeof value === 'number' ? value : undefined
}

/** 保留旧接口使用的标识与指标字段，并为展示文案和图片补充空值。 */
const normalizeSoftwareItems = (value: unknown): SoftwareItem[] => {
  if (!Array.isArray(value)) return []
  return value.filter(isSoftwareRecord).map((item) => ({
    appBundleId: getSoftwareText(item.appBundleId),
    marketId: getSoftwareMetric(item.marketId) ?? '',
    softwareId: getSoftwareText(item.softwareId),
    softwareType: getSoftwareText(item.softwareType),
    name: getSoftwareText(item.name),
    logo: getSoftwareText(item.logo),
    title: getSoftwareText(item.title),
    rating: getSoftwareMetric(item.rating),
    downloadNum: getSoftwareMetric(item.downloadNum),
    sizes: getSoftwareText(item.sizes),
    supportRegionCount: getSoftwareMetric(item.supportRegionCount),
    activeValue: getSoftwareMetric(item.activeValue),
    rankNo: Number(getSoftwareMetric(item.rankNo)) || 0,
    isShowRank: item.isShowRank === true,
    traderCode: getSoftwareText(item.traderCode),
    traderIcon: getSoftwareText(item.traderIcon),
    traderName: getSoftwareText(item.traderName),
    brandType: Number(getSoftwareMetric(item.brandType)) || 0,
  }))
}

/** 精选工具使用 result 分类列表，也兼容调用层已提取出的数组。 */
export const normalizeSoftwareToolCategories = (response: unknown): SoftwareToolCategory[] => {
  const payload = unwrapSoftwarePayload(response)
  const categories = isSoftwareRecord(payload) ? payload.result : payload
  if (!Array.isArray(categories)) throw new Error('Software tools response is missing its category list')

  return categories.filter(isSoftwareRecord).map((category) => ({
    tabName: typeof category.tabName === 'string' ? category.tabName : '',
    tabIndex: Number(category.tabIndex) || 0,
    items: normalizeSoftwareItems(category.items),
  }))
}

/** 归一化排行数据；缺少单侧列表视为空列表，空响应回退为空榜单结构。 */
export const normalizeSoftwareRankingData = (response: unknown): SoftwareRankingData => {
  const payload = unwrapSoftwarePayload(response)
  if (!payload || (Array.isArray(payload) && payload.length === 0)) {
    return { global: [], current: [] }
  }
  if (!isSoftwareRecord(payload)) {
    return { global: [], current: [] }
  }

  return {
    global: normalizeSoftwareItems(payload.global),
    current: normalizeSoftwareItems(payload.current),
  }
}

/** 选择对应范围的排行数据；当前国家与全球页签各自展示对应数据，当前国家无数据时不回退全球列表。 */
export const selectSoftwareRankingList = (
  data: SoftwareRankingData | undefined,
  scope: 'global' | 'current' = 'current',
): SoftwareItem[] => {
  if (scope === 'current') return data?.current ?? []
  return data?.global ?? []
}

/** 根据访问设备选择首屏数据，Mac/iPhone/iPad 使用 IOS，其余默认 Android。 */
export const getSoftwareDeviceType = (userAgent: string): SoftwareDeviceType => {
  return /Macintosh|MacIntel|MacPPC|Mac68K|iPhone|iPad|iPod/i.test(userAgent) ? 1 : 2
}

/** 沿用旧站数字展示方式：千分位分隔，并保留接口返回的小数精度。 */
export const formatSoftwareNumber = (value: number | string | null | undefined): string => {
  const number = Number(value)
  if (!Number.isFinite(number)) return '0'
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 20 }).format(number)
}

/**
 * 当前应用尚无软件详情页，沿用 web-wikistore 的真实外部详情地址。
 * 路径依次使用接口 marketId、appBundleId、traderCode，marketId=11 时末位为 2，否则为 1。
 */
export const getSoftwareDetailUrl = (item: SoftwareItem, languageCode: string): string => {
  if (!item.appBundleId || item.marketId === '') return ''
  const segments = [item.marketId, item.appBundleId, item.traderCode ?? '', Number(item.marketId) === 11 ? 2 : 1]
  const code = segments.map((segment) => encodeURIComponent(String(segment))).join('-')
  return `https://www.wikifx.com/${encodeURIComponent(languageCode || 'en')}/softwareDetails/${code}.html`
}
